/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import styled from "styled-components";
import {
  Box,
  FlexContainer,
  SVGIcon,
  Switch,
  Text,
  theme,
} from "@nordcloud/gnui";
import { EnvironmentType } from "~/generated/graphql";
import { TRANSITION } from "~/constants/theme";

const envTypes = [
  { envType: EnvironmentType.Production, icon: "production" },
  { envType: EnvironmentType.Staging, icon: "qa" },
  { envType: EnvironmentType.Development, icon: "settings" },
  { envType: EnvironmentType.Other, icon: "test" },
];

type EnvType = (typeof envTypes)[number]["envType"];

type SwitchEnvTypeProps = {
  onChange: (value: EnvType) => void;
  value: string;
};

export function SwitchEnvType({ onChange, value = "" }: SwitchEnvTypeProps) {
  return (
    <Box boxStyle="lightGrey" innerSpacing="spacing02">
      {envTypes.map(({ envType, icon }) => (
        <SwitchType
          key={envType}
          name={envType}
          value={envType}
          icon={icon}
          checked={value === envType}
          onChange={() => onChange(envType)}
        />
      ))}
    </Box>
  );
}

const SwitchTypeLabel = styled.label`
  cursor: pointer;
  padding: ${theme.spacing.spacing03} ${theme.spacing.spacing01};
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: opacity ${TRANSITION.default};

  &:not(:last-child) {
    border-bottom: 1px solid ${theme.color.border.border01};
  }

  &:hover {
    opacity: 0.7;
  }
`;

type SwitchTypeProps = {
  name: string;
  value: string;
  icon: string;
  checked: boolean;
  onChange: () => void;
};

function SwitchType({ name, value, checked, onChange, icon }: SwitchTypeProps) {
  return (
    <SwitchTypeLabel htmlFor={name}>
      <FlexContainer>
        {/* 
        // @ts-expect-error name prop needs to be properly typed */}
        <SVGIcon name={icon} />
        <Text
          tag="span"
          ml={theme.spacing.spacing02}
          css={{ textTransform: "capitalize" }}
        >
          {name.toLowerCase()}
        </Text>
      </FlexContainer>
      <Switch id={name} value={value} checked={checked} onChange={onChange} />
    </SwitchTypeLabel>
  );
}
